/** Canonical site URL for SEO (canonical, OG, sitemap). */
export function getSiteUrl() {
  const fromEnv = String(process.env.REACT_APP_SITE_URL || '').trim().replace(/\/$/, '')
  if (fromEnv) return fromEnv
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '')
  }
  return 'https://slimfaast.com'
}

export const SITE_NAME = 'SLIM Fast'
export const SITE_NAME_AR = 'سليم فاست'

export const DEFAULT_OG_IMAGE = '/assets/images/hero/hero-slimfast-bottle.png'

export const GLOBAL_KEYWORDS = [
  'Slimfast',
  'Slim Fast',
  'SLIM Fast',
  'slimfast',
  'slim fast saudi',
  'سليم فاست',
  'سليمفاست',
  'كبسولات slimfast',
  'منتجات التخسيس',
  'fat burning capsules',
  'weight loss saudi arabia',
  'متجر سليم فاست',
].join(', ')

/** Per-route SEO (bilingual titles/descriptions for EN + AR search). */
export const PAGE_SEO = {
  home: {
    path: '/',
    title: 'SLIM Fast | Slimfast — Official Store Saudi Arabia | سليم فاست',
    description:
      'Shop official Slimfast (SLIM Fast) capsules in Saudi Arabia. Authentic fat-burning formula, fast delivery, Tamara & card payment. سليم فاست — المتجر الرسمي للمنتجات الأصلية.',
    keywords: `${GLOBAL_KEYWORDS}, slimfast official store, slimfast saudi arabia`,
  },
  shop: {
    path: '/shop',
    title: 'Shop Slimfast Products | SLIM Fast Store | سليم فاست',
    description:
      'Browse all Slim Fast (Slimfast) products — capsules, supplements & wellness. Buy online with delivery across KSA. تسوق منتجات سليم فاست الأصلية.',
    keywords: `${GLOBAL_KEYWORDS}, buy slimfast, slimfast shop, slimfast price`,
  },
  about: {
    path: '/about',
    title: 'About Slimfast | How SLIM Fast Works | عن سليم فاست',
    description:
      'Learn how Slimfast works, our change plan, and why customers trust SLIM Fast in Saudi Arabia. تعرف على سليم فاست وكيف يساعدك في رحلة اللياقة.',
    keywords: `${GLOBAL_KEYWORDS}, about slimfast, slimfast how it works`,
  },
  ingredients: {
    path: '/ingredients',
    title: 'Slimfast Ingredients & Lab Report | SLIM Fast | مكونات سليم فاست',
    description:
      'Full Slim Fast ingredients breakdown and lab analysis. Transparent quality you can trust. مكونات سليم فاست وتحليل مخبري معتمد.',
    keywords: `${GLOBAL_KEYWORDS}, slimfast ingredients, slimfast lab report`,
  },
  contact: {
    path: '/contact',
    title: 'Contact Slimfast | SLIM Fast Support | تواصل مع سليم فاست',
    description:
      'Contact SLIM Fast (Slimfast) customer support in Saudi Arabia. WhatsApp, email & contact form. تواصل مع فريق سليم فاست.',
    keywords: `${GLOBAL_KEYWORDS}, slimfast contact, slimfast support`,
  },
}

export function productSeo(product) {
  if (!product) return null
  const name = product.name || 'Product'
  return {
    path: `/product/${product.slug}`,
    title: `${name} | Buy Slimfast | SLIM Fast | سليم فاست`,
    description:
      product.shortDescription ||
      product.description?.slice(0, 155) ||
      `Buy ${name} from the official Slimfast (SLIM Fast) store. Fast delivery across Saudi Arabia.`,
    image: product.image,
    keywords: `${GLOBAL_KEYWORDS}, ${name}, slimfast ${name}`,
    type: 'product',
  }
}

export function buildFaqJsonLd(faqItems) {
  if (!faqItems?.length) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: answer,
      },
    })),
  }
}

export function buildOrganizationJsonLd(siteUrl) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    alternateName: ['Slimfast', 'Slim Fast', SITE_NAME_AR, 'سليمفاست'],
    url: siteUrl,
    logo: `${siteUrl}/assets/images/logo/slimfast-blackheader-logo.png`,
    email: 'sslimfast2000@gmail.com',
    areaServed: {
      '@type': 'Country',
      name: 'Saudi Arabia',
    },
  }
}

export function buildWebSiteJsonLd(siteUrl) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    alternateName: ['Slimfast', SITE_NAME_AR],
    url: siteUrl,
    inLanguage: ['en', 'ar'],
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/shop`,
      },
      'query-input': 'required name=search_term_string',
    },
  }
}

export function buildProductJsonLd(product, siteUrl) {
  if (!product) return null
  const inStock = product.stock == null || Number(product.stock) > 0
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.shortDescription || product.description || product.name,
    image: product.image ? [product.image] : undefined,
    sku: product.sku || product.slug,
    brand: {
      '@type': 'Brand',
      name: SITE_NAME,
    },
    offers: {
      '@type': 'Offer',
      url: `${siteUrl}/product/${product.slug}`,
      priceCurrency: 'SAR',
      price: String(Number(product.price) || 0),
      availability: inStock
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      seller: {
        '@type': 'Organization',
        name: SITE_NAME,
      },
    },
    ...(product.ratingAvg > 0 && product.reviewCount > 0
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: String(product.ratingAvg),
            reviewCount: String(product.reviewCount),
          },
        }
      : {}),
  }
}
